import { useI18n } from '@/composables/useI18n';
import { DEFAULT_LANGUAGE, LANG_MODE_DEFAULT, LANG_MODE_ORIGINAL, LANG_MODE_TRANSLATION, LANG_TRANS, } from '@/constants';
import type { PatentData } from '@/services/patent-service.interface';
import type { LangTypeMode } from '@/services/translation/translate-service.interface';
import _ from 'lodash';
import { computed, type Ref } from 'vue';
import { useAiOptions } from './useAiOptions';
import { useOriginOptions } from './useOriginOptions';
import { useCompareUrlState } from '@/composables/useCompareUrlState';

interface OptionItem {
    label: string;
    key?: string;
    lang?: string;
    langType?: LangTypeMode;
    tip?: string;
}

const AI_TRANSLATION = {
    AIJP: '日本語 - AIpatent',
    AIEN: 'English - AIpatent',
    AICN: '简体中文 - AIpatent'
}

export function useLangSwitchOptions(patent: Ref<Partial<PatentData>>) {
    const { t } = useI18n();
    const { isCompareView } = useCompareUrlState();

    function getOptionLabel(lang: string) {
        if (lang === DEFAULT_LANGUAGE) {
            return t('patent-view["Not translated"]')
        }
        if (lang === 'NON2') {
            return t('patent-view["Not translated2"]')
        }
        return _.get({
            ...AI_TRANSLATION,
            ...LANG_TRANS,
        }, lang, '');
    }

    const { aiOptions, } = useAiOptions(patent);

    const { originOptions, } = useOriginOptions(patent);

    const switchOptions = computed<OptionItem[]>(() => {
        const items = [
            {
                key: 'NON',
                label: t('patent-view["Not translated2"]'),
                lang: 'NON',
                langType: LANG_MODE_DEFAULT,
            },
            {
                label: t('patent-view["Full translation"]'),
            },
            {
                key: 'EN',
                label: 'English',
                lang: 'EN',
                langType: LANG_MODE_TRANSLATION,
            },
            {
                key: 'CN',
                label: '简体中文',
                lang: 'CN',
                langType: LANG_MODE_TRANSLATION,
            },
            {
                key: 'JP',
                label: '日本語',
                lang: 'JP',
                langType: LANG_MODE_TRANSLATION,
            },
        ];

        // 对比视图下，没有日语翻译、AI翻译和小语种
        if (isCompareView.value) {
            items.splice(items.length - 1, 1);
            return items;
        }

        if (aiOptions.value.length > 0) {
            items.push(...aiOptions.value.map(item => ({
                key: item.lang,
                label: item.label,
                lang: item.lang,
                langType: LANG_MODE_TRANSLATION,
                tip: t('AIpatentTips'),
            })));
        }

        if (originOptions.value.length > 0) {
            items.push({
                label: t('patent-view["Original data only"]'),
            });
            items.push(...originOptions.value.map(item => ({
                key: item.lang,
                label: getOptionLabel(item.lang),
                lang: item.lang,
                langType: LANG_MODE_ORIGINAL,
            })).filter(({ label }) => label));
        }

        return items;
    });

    return {
        originOptions,
        switchOptions,
        getOptionLabel,
    };
}
